import {useMediaQuery} from '@mui/material'
import {ChangeEvent, FC, useContext, useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {addLoadingMedia, deleteLoadingMedia, messageTypes, SendMessage} from 'entities/messages'
import {setPackageModalOpen, useWallet} from 'entities/payment'
import {WarningModalContext, WarningModalContextType} from 'features/warningModal/context'
import {
  checkVideoUpload,
  GetDirectUploadUrlPhoto,
  GetDirectUploadUrlVideo,
  UploadMediaToBucket,
  uploadMediaToCF
} from 'shared/api/images'
import {createFileFormData, PRICE} from 'shared/lib'
import {Badge} from 'shared/ui/badge'
import {ArrowLeft, GoCoin20, Photo} from 'shared/ui/icons'
import {Menu} from 'shared/ui/menu'
import {getVideoDuration} from './helper'
import {Video} from './icon/icons'
import styles from './styles.module.scss'

type ChatSendMediaProps = {
  userId: string,
  sendMediaElement: HTMLDivElement | null,
  onClose: () => void
  backMedia?: () => void
  scrollBottom: () => void
}

const maxSizePhoto = 10 * 1024 * 1024 // 10mb

export const ChatSendMedia: FC<ChatSendMediaProps> = ({userId, sendMediaElement, onClose, backMedia, scrollBottom}) => {
  const dispatch = useDispatch()
  const inputRefPhoto = useRef<HTMLInputElement | null>(null)
  const inputRefVideo = useRef<HTMLInputElement | null>(null)
  const [type, setType] = useState<messageTypes.photo | messageTypes.disappearingPhoto | messageTypes.disappearingVideo>(messageTypes.photo)
  const [cfId, setCfId] = useState('')
  const [key, setKey] = useState('')
  const {refetch} = SendMessage(userId, {cfId, key}, type, dispatch)
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const {credits, freeCredits, photos, videos} = useWallet()
  const {setWarningModal} = useContext(WarningModalContext) as WarningModalContextType

  useEffect(() => {
    if (cfId || key) {
      refetch()
        .then(() => dispatch(deleteLoadingMedia(cfId || key)))
        .catch(console.error)
        .finally(() => {
          setCfId('')
          setKey('')
        })
    }
  }, [cfId, refetch, setCfId, dispatch, deleteLoadingMedia, key, setKey])

  const onClickImageHandler = (type: messageTypes.photo | messageTypes.disappearingPhoto) => {
    const pricePhoto = type === messageTypes.photo ? PRICE.CHAT_PHOTO_SEND : PRICE.CHAT_DISAPPEARING_PHOTO_SEND
    if (!photos && credits + freeCredits < pricePhoto) {
      return dispatch(setPackageModalOpen(true))
    }

    setType(type)
    inputRefPhoto.current?.click()
  }
  const onClickVideoHandler = () => {
    if (!videos && credits + freeCredits < PRICE.CHAT_DISAPPEARING_VIDEO_SEND) {
      return dispatch(setPackageModalOpen(true))
    }

    setType(messageTypes.disappearingVideo)
    inputRefVideo.current?.click()
  }

  const onChangeImageHandler = async (event: ChangeEvent<HTMLInputElement>) => {
    onClose()
    event.preventDefault()
    if (!(event.target.files && event.target.files[0])) return
    const image = event.target.files[0]

    if (image.size > maxSizePhoto) {
      setWarningModal({header: 'File size is too large', text: 'Maximum file size 10mb'})
      event.target.value = ''
      return
    }

    const {data: [uploadData]} = await GetDirectUploadUrlPhoto({count: 1, mediaType: 'attachment'})
    if (!uploadData) return event.target.value = ''

    dispatch(addLoadingMedia({cfId: uploadData.key, previewUrl: URL.createObjectURL(image), type: 'photo'}))
    setTimeout(scrollBottom)

    try {
      await UploadMediaToBucket(uploadData.uploadUrl, image)

      setKey(uploadData.key)
    } catch (e) {
      console.error(e)
    } finally {
      event.target.value = ''
    }
  }

  const onChangeVideoHandler = async (event: ChangeEvent<HTMLInputElement>) => {
    onClose()
    event.preventDefault()
    if (!(event.target.files && event.target.files[0])) return
    const video = event.target.files[0]

    const duration = await getVideoDuration(video)
    if (duration > 30) {
      setWarningModal({header: 'Video is too long', text: 'The video is longer than 30 seconds'})
      event.target.value = ''
      return
    }

    const {data: [uploadData]} = await GetDirectUploadUrlVideo({count: 1, mediaType: 'attachment'})
    if (!uploadData) return event.target.value = ''

    dispatch(addLoadingMedia({cfId: uploadData.cfId, previewUrl: URL.createObjectURL(video), type: 'video'}))
    setTimeout(scrollBottom)

    const formData = await createFileFormData(video)
    await uploadMediaToCF(uploadData.uploadUrl, formData)

    const interval = setInterval(async () => {
      const {data: status} = await checkVideoUpload(uploadData.cfId)

      if (status.ready) {
        setCfId(uploadData.cfId)
        clearInterval(interval)
      }
    }, 3000)

    event.target.value = ''
  }

  return (
    <Menu
      anchorEl={sendMediaElement}
      open={Boolean(sendMediaElement)}
      onClose={onClose}
      anchorOrigin={{
        vertical: is768p ? -4 : -16,
        horizontal: 46
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
    >
      <div className={styles.popup}>
        {is768p ? <div className={styles.header} onClick={backMedia}>
          <div className={styles.arrowLeft}><ArrowLeft /></div>
          Media
        </div> : null}

        <div className={styles.item} onClick={() => onClickImageHandler(messageTypes.photo)}>
          <div className={styles.info}>
            <div className={styles.title}><Photo /> Photo</div>
            <div className={styles.priceWrapper}>
              <div className={styles.price}>{PRICE.CHAT_PHOTO_SEND} <GoCoin20 />/ photo</div>
            </div>
          </div>
          <Badge>Free x{photos}</Badge>
        </div>
        <div className={styles.item} onClick={() => onClickImageHandler(messageTypes.disappearingPhoto)}>
          <div className={styles.info}>
            <div className={styles.title}><Photo /> Disappearing photo</div>
            <div className={styles.priceWrapper}>
              <div className={styles.price}>{PRICE.CHAT_DISAPPEARING_PHOTO_SEND} <GoCoin20 />/ photo</div>
              <Badge>Free x{photos}</Badge>
            </div>
            <div className={styles.subText}>Deleted after 12 hours.</div>
          </div>
        </div>
        <div className={styles.item} onClick={onClickVideoHandler}>
          <div className={styles.info}>
            <div className={styles.title}><Video />Disappearing video</div>
            <div className={styles.priceWrapper}>
              <div className={styles.price}>{PRICE.CHAT_DISAPPEARING_VIDEO_SEND} <GoCoin20 />/ video</div>
              <Badge>Free x{videos}</Badge>
            </div>
            <div className={styles.subText}>Deleted after 12 hours.</div>
          </div>
        </div>

        <input
          ref={inputRefPhoto}
          type='file'
          accept='.jpg, .jpeg, .png, .heif, .heic'
          onChange={onChangeImageHandler}
          className={styles.mediaInput}
        />
        <input
          ref={inputRefVideo}
          type='file'
          accept='.hevc, .mp4, .mov'
          onChange={onChangeVideoHandler}
          className={styles.mediaInput}
        />
      </div>
    </Menu>
  )
}